import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { MailCheckIcon, ArrowRightIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { AuthShell } from '../../components/portal/AuthShell';
import { usePortalAuth } from '../../context/PortalAuth';
import { LIVE_API } from '../../lib/config';
const DEMO_ADDRESSES = [{
  email: 'a.mensah@northbridge.org',
  note: 'Owner at one organisation, viewer at another'
}, {
  email: 'f.bah@northbridge.org',
  note: 'Member, sees assigned projects only'
}, {
  email: 'j.diallo@northbridge.org',
  note: 'Viewer, read only'
}, {
  email: 'k.osei@northbridge.org',
  note: 'Invited, first sign in leads to onboarding'
}];
export function PortalLogin() {
  const {
    user,
    loading,
    requestMagicLink
  } = usePortalAuth();
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const [previewLink, setPreviewLink] = React.useState<string | undefined>();
  if (!loading && user) return <Navigate to="/portal/dashboard" replace data-id="element-3049" data-mp="38pv1eikchnng" />;
  const send = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setError(null);
    setStatus('sending');
    const result = await requestMagicLink(email);
    if (!result.ok) {
      setStatus('idle');
      setError(result.error ?? 'We could not send a link. Please try again.');
      return;
    }
    setPreviewLink(result.previewLink);
    setStatus('sent');
  };
  if (status === 'sent') {
    return <AuthShell title="Check your inbox" data-id="element-3050" data-mp="3dmlj1abssraz">
        <MailCheckIcon className="h-9 w-9 text-gold" aria-hidden="true" data-id="element-3051" data-mp="3dpzhi7hccgd5" />
        <h1 className="mt-6 font-display text-[30px] font-light text-ink" data-id="element-3052" data-mp="3il6jnauypczy">Check your inbox</h1>
        <p className="mt-4 text-[16px] leading-relaxed text-body" role="status" data-id="element-3053" data-mp="226wu4dezhi7j">
          If <span className="text-ink" data-id="element-3054" data-mp="278cee39c7kov">{email.trim()}</span> has access to the portal, a sign in link is on its way. It
          works once and expires in 15 minutes.
        </p>

        {!LIVE_API && <div className="mt-8 rounded-md border border-line bg-panel p-5" data-id="element-3055" data-mp="7tkj3h7twbzk">
            <p className="text-[14px] font-medium text-ink" data-id="element-3056" data-mp="30rhh27npjrp">Preview</p>
            {previewLink ? <>
                <p className="mt-1 text-[14px] text-body" data-id="element-3057" data-mp="3v4kaawc7aaga">No email is sent from the preview. Open the link that would have arrived.</p>
                <Button to={previewLink} className="mt-4" data-id="element-3058" data-mp="3q3w7zeiiu76r">
                  Open the sign in link
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3059" data-mp="3l9il60hxd8dp" />
                </Button>
              </> : <p className="mt-1 text-[14px] text-body" data-id="element-3060" data-mp="3c6zgrbyhqnpc">
                This address has not been invited, so no link was issued. The message above stays the same either way,
                so the form never reveals who holds an account.
              </p>}
          </div>}

        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[14px]" data-id="element-3061" data-mp="2sd9bxblgs41n">
          <button type="button" onClick={() => void send()} className="text-royal hover:underline" data-id="element-3062" data-mp="2sawtkxbsnro2">
            Send another link
          </button>
          <button type="button" onClick={() => setStatus('idle')} className="text-muted hover:text-ink" data-id="element-3063" data-mp="323lxn750m39d">
            Use a different email
          </button>
        </div>
        {error && <p role="alert" className="mt-4 text-[14px] text-[#FF8B7A]" data-id="element-3064" data-mp="29ex35qygsw6l">
            {error}
          </p>}
      </AuthShell>;
  }
  return <AuthShell title="Client portal sign in" data-id="element-3065" data-mp="2eet5llrikfk1">
      <h1 className="font-display text-[30px] font-light text-ink" data-id="element-3066" data-mp="2idig96l63izz">Sign in to the client portal</h1>
      <p className="mt-3 text-[16px] text-body" data-id="element-3067" data-mp="2nepn93fhke5l">Enter your work email and we will send you a secure sign in link. There is no password to remember.</p>

      <form onSubmit={send} noValidate className="mt-8 space-y-5" data-id="element-3068" data-mp="3tw2zlhumeyn4">
        <Field id="portal-email" label="Work email" required error={error ?? undefined} data-id="element-3069" data-mp="2lum1ixjg4kv">
          <input id="portal-email" type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} className={inputClass} aria-invalid={error ? true : undefined} aria-describedby={error ? 'portal-email-error' : undefined} data-id="element-3070" data-mp="171o6evpwee7" />
        </Field>
        <Button type="submit" size="lg" disabled={status === 'sending' || !email.trim()} className="w-full" data-id="element-3071" data-mp="k3tc51m7fhol">
          {status === 'sending' ? 'Sending your link' : 'Email me a sign in link'}
        </Button>
      </form>

      <p className="mt-6 text-[14px] text-muted" data-id="element-3072" data-mp="tx0gcj57krn3">
        Not yet a client? Access comes with an engagement. <Link to="/contact" className="text-royal hover:underline" data-id="element-3073" data-mp="oxxltsmuu3td">Talk to us</Link>.
      </p>

      {!LIVE_API && <details className="mt-10 rounded-md border border-line bg-panel" data-id="element-3074" data-mp="xwj3llki5et3">
          <summary className="cursor-pointer px-5 py-4 text-[14px] font-medium text-ink" data-id="element-3075" data-mp="xug2a5dwfz92">Preview accounts</summary>
          <ul className="divide-y divide-line border-t border-line" data-id="element-3076" data-mp="1hghu37hskbpf">
            {DEMO_ADDRESSES.map(demo => <li key={demo.email} data-id="element-3077" data-mp="18hwglqr6fmvi">
                <button type="button" onClick={() => setEmail(demo.email)} className="w-full px-5 py-3 text-left transition-colors duration-150 hover:bg-charcoal" data-id="element-3078" data-mp="18gut2msz57ww">
                  <span className="block text-[14px] text-ink" data-id="element-3079" data-mp="yq077jjn4abz">{demo.email}</span>
                  <span className="block text-[13px] text-muted" data-id="element-3080" data-mp="13juned01e76b">{demo.note}</span>
                </button>
              </li>)}
          </ul>
        </details>}
    </AuthShell>;
}